"use client";

import { useEnrollmentRounds } from "./useEnrollmentRounds";

/**
 * Home enrollment banner — DEFAULT look (section type `enrollmentGate`).
 * A doorway to /enroll, where the whole flow lives. Data comes from the
 * shared useEnrollmentRounds hook; themes with their own idiom render their
 * own banner off the same hook (crystal, um1sf, ci926, dental06, epona).
 */
export function EnrollmentGateSection({
  section,
  currentLanguage = "mm",
}: {
  section: any;
  currentLanguage?: "en" | "mm";
}) {
  const d = useEnrollmentRounds(section, currentLanguage);
  if (!d) return null;

  return (
    <section className="mx-auto w-full max-w-6xl px-4 py-6" lang={d.lang === "mm" ? "my" : "en"}>
      <div className="overflow-hidden rounded-2xl bg-primary text-primary-foreground shadow-lg shadow-primary/25">
        {d.rounds.map((r, i) => (
          <div
            key={r.id}
            className={`flex flex-col gap-4 px-5 py-5 sm:flex-row sm:items-center sm:px-7 ${
              i > 0 ? "border-t border-primary-foreground/15" : ""
            }`}
          >
            <span className="hidden h-12 w-12 shrink-0 items-center justify-center rounded-full bg-primary-foreground/15 sm:flex">
              <svg viewBox="0 0 24 24" className="h-6 w-6" fill="none" stroke="currentColor"
                strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <path d="M22 10L12 5 2 10l10 5 10-5z" />
                <path d="M6 12v5c0 1.7 2.7 3 6 3s6-1.3 6-3v-5" />
              </svg>
            </span>
            <span className="min-w-0 flex-1">
              <span className="block text-[11px] font-bold uppercase tracking-widest text-primary-foreground/75">
                {d.title}
              </span>
              <span className="mt-0.5 block text-base font-bold leading-snug sm:text-lg">{r.label}</span>
              {r.window && (
                <span className="mt-0.5 block text-sm text-primary-foreground/85">
                  {d.t.windowPrefix} {r.window}
                </span>
              )}
            </span>
            <span className="flex shrink-0 flex-wrap gap-2">
              {r.listHref && (
                <a href={r.listHref}
                  className="inline-flex h-11 items-center rounded-lg bg-primary-foreground/15 px-4 text-sm font-semibold hover:bg-primary-foreground/25">
                  {d.t.list}
                </a>
              )}
              <a href={r.enrollHref}
                className="inline-flex h-11 items-center rounded-lg bg-primary-foreground px-5 text-sm font-bold text-primary shadow-sm transition-transform hover:scale-105">
                {d.t.enroll} →
              </a>
            </span>
          </div>
        ))}
      </div>
    </section>
  );
}
